import { Card } from "@/components/ui/card";
import { graphql, useFragment, useMutation } from "react-relay";
import type { courseQuiz_quiz$key } from "./__generated__/courseQuiz_quiz.graphql";
import { useEditor } from "@/editor/editor";
import { Link, useRouteContext } from "@tanstack/react-router";
import { EditorContent } from "@tiptap/react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useForm } from "@tanstack/react-form";
import z from "zod";
import { cn } from "@/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/ui/field";
import { Icon } from "@/components/icon";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import type { courseQuizMutation } from "./__generated__/courseQuizMutation.graphql";
import { useState } from "react";
import confetti from "canvas-confetti";
import { Dialog } from "@/components/ui/dialog";
import { CertificateEmitDialog } from "@/dialogs/certificate-emit-dialog";

export function CourseQuiz({ quiz }: { quiz: courseQuiz_quiz$key }) {
	const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
	const { club } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();
	const data = useFragment<courseQuiz_quiz$key>(
		graphql`
    fragment courseQuiz_quiz on CourseQuiz {
      id
      name
      slug
      description
      completed
      quiz {
        id
        questions(enabled: ACTIVE) {
          id
          title
          description
          active
          options(enabled: ACTIVE) {
            id
            active
            text
          }
        }
      }

      course {
        slug
        ...certificateEmitDialogFragment
      }

      nextClass {
        canAccess
        slug
      }
      previousClass {
        canAccess
        slug
      }
    }
  `,
		quiz,
	);

	const [mutate] = useMutation<courseQuizMutation>(graphql`
    mutation courseQuizMutation($input: CreateCourseQuizAnswerInput!, $includeQuestions: [String!]!) {
      courseQuizAnswer(courseQuizAnswerInput: $input) {
          id
          isCorrect
          quiz {
            id
            questions(includes: $includeQuestions) {
              id
              answer {
                id
              }
            }
            courseClass {
              slug
              course {
                id
                slug
                certificateEmission
                completed
                certificate {
                  id
                }
              }
            }
          }
      }
    }
	`);

	const editor = useEditor({
		editable: false,
		content: data.description,
		features: {
			placeholder: { enabled: true },
			colors: { enabled: true },
			attachments: { enabled: true },
			video: { enabled: true },
			poll: { enabled: true },
			youtube: { enabled: true },
			image: { enabled: true },
			iframe: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			styling: { enabled: true },
		},
		club: club.slug,
	});

	const [optionStatus, setOptionStatus] = useState<{
		questions: { id: string; status: boolean }[];
	} | null>(null);
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState<
		boolean | null
	>(null);

	const form = useForm({
		defaultValues: {
			options: data.quiz.questions.map(() => null) as Array<string | null>,
		},
		onSubmit: ({ value }) => {
			return mutate({
				variables: {
					input: {
						quiz: data.quiz.id,
						questionAnswers: value.options.map((op, index) => ({
							question: data.quiz.questions[index].id,
							option: op!,
						})),
					},
					includeQuestions: data.quiz.questions.map((q) => q.id),
				},
				onCompleted: (response) => {
					setSubmittedSuccessfully(response.courseQuizAnswer.isCorrect);

					track(AnalyticsEvents.COURSE_QUIZ_SUBMITTED, {
						lesson_id: data.id,
						course_slug: data.course.slug,
					});

					setOptionStatus({
						questions: response.courseQuizAnswer.quiz.questions.map((q) => ({
							id: q.id,
							status: Boolean(q.answer && value.options.includes(q.answer.id)),
						})),
					});

					if (
						response.courseQuizAnswer.quiz.courseClass.course.completed &&
						response.courseQuizAnswer.quiz.courseClass.course
							.certificateEmission &&
						!response.courseQuizAnswer.quiz.courseClass.course.certificate
					) {
						setTimeout(() => {
							setCertificateDialogOpen(true);
						}, 5000);
					}

					if (response.courseQuizAnswer.isCorrect) {
						toast.success("Quiz completado com sucesso!");
						showConfetti();
					} else if (!response.courseQuizAnswer.isCorrect) {
						toast.error(
							"Você não completou o quiz com sucesso. Tente novamente para avançar com o conteúdo do curso.",
						);
					}
				},
			});
		},
		validators: {
			onSubmit: z
				.object({
					options: z
						.array(z.string().nullable())
						.length(data.quiz.questions.length),
				})
				.superRefine((val, ctx) => {
					for (const option of val.options) {
						if (!option) {
							ctx.addIssue({
								code: "custom",
								message: "Opção Inválida",
								path: ["options", val.options.indexOf(option)],
							});
						}
					}
				}),
		},
	});

	const showConfetti = () => {
		const end = Date.now() + 3 * 1000; // 3 seconds
		const colors = ["#a786ff", "#fd8bbc", "#eca184", "#f8deb1"];

		const frame = () => {
			if (Date.now() > end) return;
			confetti({
				particleCount: 2,
				angle: 60,
				spread: 55,
				startVelocity: 60,
				origin: { x: 0, y: 1 },
				colors: colors,
			});
			confetti({
				particleCount: 2,
				angle: 120,
				spread: 55,
				startVelocity: 60,
				origin: { x: 1, y: 1 },
				colors: colors,
			});
			requestAnimationFrame(frame);
		};

		frame();
	};

	return (
		<Dialog>
			<CertificateEmitDialog
				open={certificateDialogOpen}
				onClose={() => setCertificateDialogOpen(false)}
				course={data.course}
			/>
			<Card
				className={
					"flex-1 w-full overflow-hidden h-fit max-md:shadow-none max-md:border-0 max-md:py-3 max-md:px-0"
				}
			>
				<div className="font-semibold text-2xl/6 px-6 max-md:px-3">
					{data.name}
				</div>

				<form
					className="px-6 max-md:px-3"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					{editor && !editor.isEmpty ? (
						<EditorContent editor={editor} />
					) : (
						<div className="text-sm text-muted-foreground pb-6">
							Nenhuma descrição disponível
						</div>
					)}

					<Separator />

					<div className="flex flex-col gap-6 mt-6">
						{data.quiz.questions.map((question, index) => (
							<form.Field key={question.id} name={`options[${index}]`}>
								{(field) => (
									<div key={question.id}>
										<span>
											{index + 1}. {question.title}
										</span>

										{field.state.meta.errors.length ? (
											<FieldError
												className="mt-2"
												errors={field.state.meta.errors}
											/>
										) : null}

										<RadioGroup
											className="mt-4"
											value={field.state.value}
											disabled={form.state.isSubmitted}
											onValueChange={(value) => field.setValue(value)}
										>
											{question.options.map((op) => (
												<div key={op.id} className="flex items-center gap-3">
													<RadioGroupItem value={op.id} />
													<Label
														className={cn(
															Boolean(
																field.state.value === op.id &&
																	typeof submittedSuccessfully === "boolean" &&
																	optionStatus?.questions.find(
																		(q) =>
																			q.id === data.quiz.questions[index].id,
																	)?.status,
															) && "text-primary",
															Boolean(
																field.state.value === op.id &&
																	typeof submittedSuccessfully === "boolean" &&
																	!optionStatus?.questions.find(
																		(q) =>
																			q.id === data.quiz.questions[index].id,
																	)?.status,
															) && "text-destructive",
														)}
													>
														{op.text}
													</Label>
												</div>
											))}
										</RadioGroup>
									</div>
								)}
							</form.Field>
						))}
					</div>

					<div className="grid grid-cols-3 gap-3 mt-6">
						{data.previousClass?.canAccess ? (
							<div className="flex flex-row items-center justify-start">
								<Link
									to="/cursos/$course/$lesson"
									params={{
										course: data.course.slug,
										lesson: data.previousClass.slug,
									}}
								>
									<Button
										variant="ghost"
										className="flex flex-row items-center"
									>
										<Icon name="ChevronLeft" className="size-4" />

										<span className="max-lg:hidden">Aula Anterior</span>
										<span className="lg:hidden">Anterior</span>
									</Button>
								</Link>
							</div>
						) : (
							<Button
								variant="ghost"
								className="flex flex-row items-center"
								disabled
							>
								<Icon name="ChevronLeft" className="size-4" />
								<span className="max-lg:hidden">Aula Anterior</span>
								<span className="lg:hidden">Anterior</span>
							</Button>
						)}

						{submittedSuccessfully ? (
							<div className="flex flex-row items-center justify-center">
								<Button
									variant="secondary"
									type="submit"
									className="flex flex-row items-center"
									disabled
								>
									<Icon name="Check" className="size-4" />
									Concluído
								</Button>
							</div>
						) : null}

						{form.state.isSubmitSuccessful &&
						typeof submittedSuccessfully === "boolean" &&
						!submittedSuccessfully ? (
							<Link
								to="/cursos/$course/$lesson"
								reloadDocument
								params={{ course: data.course.slug, lesson: data.slug }}
								className="flex flex-row items-center justify-center"
							>
								<Button
									variant="destructive"
									type="submit"
									className="flex flex-row items-center"
								>
									<Icon name="RotateCcw" className="size-4" />
									Tentar Novamente
								</Button>
							</Link>
						) : null}

						{!form.state.isSubmitSuccessful ||
						typeof submittedSuccessfully !== "boolean" ? (
							<form.Subscribe selector={(state) => [state.isSubmitting]}>
								{([isSubmitting]) => (
									<div className="flex flex-row items-center justify-center">
										<Button
											variant="outline"
											type="submit"
											className="flex flex-row items-center"
											disabled={isSubmitting}
										>
											{isSubmitting && <Spinner />}
											Confirmar
										</Button>
									</div>
								)}
							</form.Subscribe>
						) : null}

						{data.nextClass?.canAccess ? (
							<div className="flex flex-row items-center justify-end">
								<Link
									to="/cursos/$course/$lesson"
									params={{
										course: data.course.slug,
										lesson: data.nextClass.slug,
									}}
								>
									<Button
										variant="ghost"
										className="flex flex-row items-center"
									>
										<span className="max-lg:hidden">Próxima Aula</span>
										<span className="lg:hidden">Próxima</span>
										<Icon name="ChevronRight" className="size-4" />
									</Button>
								</Link>
							</div>
						) : null}
					</div>
				</form>
			</Card>
		</Dialog>
	);
}
